/**
 * Arquivo: src/components/AnexosLancamento.tsx
 * Versão: 5.5.2
 * Data: 02/10/2026
 * Componente reutilizável de comprovantes e anexos por módulo + registro_id.
 */
import { useEffect, useState } from 'react'
import { ExternalLink, Paperclip, Trash2, Upload } from 'lucide-react'
import Modal from './Modal'
import ConfirmDialog from './ConfirmDialog'
import { abrirAnexo, excluirAnexo, listarAnexos, enviarAnexo, type Anexo, type ModuloAnexo } from '../services/drive'

export default function AnexosLancamento({
  aberto,baseId,registroId,modulo,titulo,podeEditar,onClose,
}:{
  aberto:boolean
  baseId:string
  registroId:string
  modulo:ModuloAnexo
  titulo:string
  podeEditar:boolean
  onClose:()=>void
}){
  const [itens,setItens]=useState<Anexo[]>([])
  const [erro,setErro]=useState('')
  const [busy,setBusy]=useState(false)
  const [excluirPendente,setExcluirPendente]=useState<Anexo|null>(null)

  const carregar=async()=>{
    if(!aberto)return
    setErro('')
    try{setItens(await listarAnexos(baseId,modulo,registroId))}
    catch(e){
      console.error('Falha ao carregar anexos:',e)
      setErro('Não foi possível carregar os anexos. Tente novamente.')
    }
  }

  useEffect(()=>{void carregar()},[aberto,baseId,modulo,registroId])

  const upload=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=Array.from(e.target.files||[])
    if(!files.length)return
    setBusy(true);setErro('')
    try{
      for(const f of files)await enviarAnexo(baseId,modulo,registroId,f)
      await carregar()
    }catch(x){
      console.error('Falha ao enviar anexo:',x)
      setErro('Não foi possível anexar o arquivo. Tente novamente.')
    }
    finally{setBusy(false);e.target.value=''}
  }

  const visualizar=async(a:Anexo)=>{
    setErro('')
    try{await abrirAnexo(baseId,modulo,registroId,a.id)}
    catch(x){
      console.error('Falha ao abrir anexo:',x)
      setErro(x instanceof Error?x.message:'Não foi possível abrir o arquivo.')
    }
  }

  return <Modal aberto={aberto} titulo="Comprovantes e anexos" subtitulo={titulo} largo onClose={onClose}>
    {erro&&<div className="error-box page-error">{erro}</div>}
    <div className="attachment-toolbar">
      <div>
        <strong>{itens.length} arquivo(s)</strong>
        <small>PDF, JPG, JPEG e PNG. Nomes duplicados no mesmo registro são bloqueados.</small>
      </div>
      {podeEditar&&<label className="button primary compact">
        <Upload size={15}/>{busy?'Enviando...':'Adicionar arquivos'}
        <input hidden multiple disabled={busy} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={upload}/>
      </label>}
    </div>
    <div className="attachment-list">
      {itens.map(a=><div className="attachment-item" key={a.id}>
        <Paperclip size={17}/>
        <div>
          <strong>{a.nome_arquivo}</strong>
          <small>{a.tamanho?`${(a.tamanho/1024).toFixed(1)} KB`:''}</small>
        </div>
        <div className="action-row">
          <button type="button" className="table-action" title="Abrir arquivo" aria-label="Abrir arquivo" onClick={()=>void visualizar(a)}><ExternalLink size={15}/></button>
          {podeEditar&&<button className="table-action danger" title="Remover" onClick={()=>setExcluirPendente(a)}><Trash2 size={15}/></button>}
        </div>
      </div>)}
      {!itens.length&&<div className="empty-state">Nenhum comprovante anexado.</div>}
    </div>

    <ConfirmDialog
      aberto={!!excluirPendente}
      titulo="Remover anexo"
      mensagem={excluirPendente?`Deseja remover o arquivo “${excluirPendente.nome_arquivo}”? Esta ação também excluirá o arquivo do OneDrive.`:''}
      confirmarTexto="Remover"
      perigo
      onCancelar={()=>setExcluirPendente(null)}
      onConfirmar={async()=>{
        if(!excluirPendente)return
        setBusy(true)
        setErro('')
        try{
          await excluirAnexo(baseId,modulo,registroId,excluirPendente.id)
          setExcluirPendente(null)
          await carregar()
        }catch(x){
          console.error('Falha ao remover anexo:',x)
          setErro('Não foi possível remover o anexo. Tente novamente.')
        }finally{
          setBusy(false)
        }
      }}
    />
  </Modal>
}
